import React from 'react'
import { cn } from '../utlis/cn'

type ButtonProps = {
    className?: string
    variant?: 'primary' | 'secondary' | 'glow' | 'rectangle' | 'basic' | 'tertiary' | 'link' | 'link-dark'
    children?: React.ReactNode
    minimal?: boolean
    forwardedRef?: React.ForwardedRef<HTMLButtonElement>
} & React.ButtonHTMLAttributes<HTMLButtonElement>

const commonBorder = 'rounded-full'
const commonStyle = 'inline-flex items-center justify-center py-[8px] px-4 cursor-pointer'

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    { className, variant, children, minimal, ...rest },
    ref
) {
    if (variant === 'primary') {
        return <PrimaryButton className={className} children={children} {...rest} forwardedRef={ref} />
    }
    if (variant === 'secondary') {
        return <SecondaryButton className={className} children={children} {...rest} forwardedRef={ref} />
    }
    if (variant === 'tertiary') {
        return <TertiaryButton className={className} children={children} {...rest} forwardedRef={ref} />
    }
    if (variant === 'glow') {
        return <GlowButton className={className} children={children} {...rest} forwardedRef={ref} />
    }
    if (variant === 'rectangle') {
        return <RectangleButton className={className} children={children} {...rest} forwardedRef={ref} />
    }
    const linkStyle = (() => {
        if (variant === 'link') {
            return 'text-muse-yellow hover:underline'
        } else if (variant === 'link-dark') {
            return 'font-bold hover:underline'
        }
    })()

    return (
        <button className={cn('cursor-pointer', linkStyle, className)} {...rest} ref={ref}>
            {children}
        </button>
    )
})

function PrimaryButton({ className, children, forwardedRef, ...rest }: ButtonProps) {
    const style = 'text-black font-medium'
    const bg =
        'bg-[radial-gradient(circle_at_0.42%_9.62%,#FCE640_0%,#F49629_77.07%,#F17E22_100%)] enabled:hover:bg-[radial-gradient(ellipse_140%_140%_at_0.42%_9.62%,#FCE640_0%,#F49629_40%,#F17E22_50%)]'
    const disabled = 'disabled:opacity-50'

    return (
        <button className={cn(commonStyle, style, bg, commonBorder, className, disabled)} {...rest} ref={forwardedRef}>
            {children}
        </button>
    )
}

function SecondaryButton({ className, children, forwardedRef, ...rest }: ButtonProps) {
    const style = 'transparent border-muse-toplit hover:bg-white/5'
    const disabledStyle = 'cursor-not-allowed opacity-70'

    return (
        <button
            className={cn(commonStyle, style, commonBorder, rest.disabled && disabledStyle, className)}
            {...rest}
            ref={forwardedRef}
        >
            {children}
        </button>
    )
}

const rectangleButtonStyles = [
    'flex',
    'py-[8px] pl-3 pr-6',
    'rounded-[12px]',
    'bg-[rgba(255,255,255,0.03)]',
    'shadow-glow',
    'border-muse-toplit-low',
    'items-center',
    'gap-[10px]',
    'focus:outline-0',
    'cursor-pointer',
]

const buttonStyles = {
    disabled: ['opacity-50 cursor-not-allowed'],
    enabled: ['hover:bg-[rgba(255,255,255,0.15)]', 'active:bg-[rgba(255,255,255,0.08)] active:border-white/15'],
}

function RectangleButton({ className, children, forwardedRef, ...rest }: ButtonProps) {
    return (
        <button
            className={cn(
                rectangleButtonStyles,
                rest.disabled ? buttonStyles.disabled : buttonStyles.enabled,
                className
            )}
            {...rest}
            ref={forwardedRef}
        >
            {children}
        </button>
    )
}

const tertiaryButtonStyles = [
    'cursor-pointer',
    'flex',
    'w-[36px]',
    'h-[36px]',
    'shrink-0',
    'justify-center',
    'items-center',
    'justify-center',
    'items-center',
    'rounded-[14px]',
    'border-muse-toplit-low',
    'bg-[rgba(248,248,248,0.01)]',
    'shadow-glow',
]

// FIXME: What are the appropriate styles here?
const tertiaryButtonStateStyles = {
    disabled: ['opacity-50'],
    enabled: ['hover:bg-[rgba(248,248,248,0.08)]', 'active:bg-[rgba(248,248,248,0.03)]'],
}

function TertiaryButton({ className, children, disabled, minimal, forwardedRef, ...rest }: ButtonProps) {
    return (
        <button
            className={cn(
                tertiaryButtonStyles,
                disabled ? tertiaryButtonStateStyles.disabled : tertiaryButtonStateStyles.enabled,
                className,
                minimal ? 'w-[38px] h-[38px]' : ''
            )}
            {...rest}
            ref={forwardedRef}
        >
            {children}
        </button>
    )
}

function GlowButton({ className, children, forwardedRef, ...rest }: ButtonProps) {
    const style = 'relative overflow-clip transparent border border-muse-toplit hover:bg-white/5'
    const bg = 'shadow-glow'

    return (
        <button className={cn(commonStyle, style, commonBorder, bg, className)} {...rest} ref={forwardedRef}>
            <div className="absolute bottom-0 bg-white left-0 right-0 h-[2px] blur-md"></div>
            {children}
        </button>
    )
}
